import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

// ---------------------------------------------------------------------------
// Small wrapper around three's GLTFLoader for both URL and ArrayBuffer
// (drag-drop) sources. Stays raw rather than reaching for drei's useGLTF
// because the loaded-assets list is imperative (user adds entries, picks
// one as active) and useGLTF wants URLs known at render time inside a
// Suspense boundary.
//
// The future in-game integration of any squirrel asset will use the same
// raw GLTFLoader path. Drei is fine where it earns its keep; here the raw
// loader is more direct.
// ---------------------------------------------------------------------------

export interface LoadedGltfAsset {
  id: string;
  name: string;
  source: 'drag-drop' | 'url';
  /** The raw GLTF payload — `gltf.scene` is the renderable group. */
  gltf: GLTF;
  /** Computed once at load time; used by the camera framer + inspector. */
  bbox: THREE.Box3;
}

const loader = new GLTFLoader();

let nextId = 0;
function genId(): string {
  return `loaded-${++nextId}`;
}

/**
 * Load a GLTF/GLB from an ArrayBuffer (e.g. from a dropped File).
 * The second argument to parse is a base path used to resolve external
 * resources; for self-contained .glb files we pass an empty string.
 */
export function loadFromArrayBuffer(
  data: ArrayBuffer,
  fileName: string
): Promise<LoadedGltfAsset> {
  return new Promise((resolve, reject) => {
    loader.parse(
      data,
      '',
      (gltf: GLTF) => {
        const bbox = new THREE.Box3().setFromObject(gltf.scene);
        resolve({
          id: genId(),
          name: fileName,
          source: 'drag-drop',
          gltf,
          bbox,
        });
      },
      (err: unknown) => reject(err)
    );
  });
}

/**
 * Load a GLTF/GLB from a URL. Doesn't attempt to share cache with drei's
 * useGLTF — viewer loads are one-shot.
 */
export function loadFromUrl(url: string): Promise<LoadedGltfAsset> {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf: GLTF) => {
        const bbox = new THREE.Box3().setFromObject(gltf.scene);
        resolve({
          id: genId(),
          name: deriveNameFromUrl(url),
          source: 'url',
          gltf,
          bbox,
        });
      },
      undefined,
      (err: unknown) => reject(err)
    );
  });
}

function deriveNameFromUrl(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    const tail = u.pathname.split('/').pop();
    return tail || url;
  } catch {
    return url;
  }
}
